import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  PageHeader, SectionCard, DataTable, EmptyState, ActionButton, SearchBar,
  FilterBar, LoadingState, StatusBadge,
} from "@/components/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SalesNav, STATUS_TONES, usd, fmtDate } from "./common";
import CreateEstimateWizard from "./CreateEstimateWizard";

const FILTER_STATUSES = ["draft", "ready_for_review", "internal_review", "approved", "sent", "viewed",
  "accepted", "payment_pending", "paid", "declined", "expired", "voided", "converted"];

export default function Estimates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [presetClient, setPresetClient] = useState("");

  // Single canonical entry point. Deep-links open the wizard directly:
  //   /sales/estimates?create=1&client={id}
  useEffect(() => {
    if (searchParams.get("create")) {
      setPresetClient(searchParams.get("client") || "");
      setWizardOpen(true);
      searchParams.delete("create"); searchParams.delete("client");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/sales/estimates", { params });
      setRows(data || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [query, statusFilter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const columns = [
    { key: "estimate_number", label: "Estimate #" },
    { key: "client_name", label: "Client" },
    { key: "total", label: "Amount", align: "right" },
    { key: "status", label: "Status" },
    { key: "assigned_user_name", label: "Assigned", className: "hidden lg:table-cell" },
    { key: "expiration_date", label: "Expires", className: "hidden md:table-cell" },
    { key: "last_activity_at", label: "Last Activity", className: "hidden xl:table-cell" },
  ];
  const renderCell = (r, c) => {
    switch (c.key) {
      case "estimate_number": return <span className="font-mono text-xs text-slate-500">{r.estimate_number}</span>;
      case "total": return <span className="font-medium text-slate-800">{usd(r.total, r.currency)}</span>;
      case "status": return <StatusBadge status={r.status} map={STATUS_TONES} />;
      case "expiration_date": case "last_activity_at": return <span className="text-slate-500">{fmtDate(r[c.key])}</span>;
      default: return <span className="text-slate-700">{r[c.key] || "—"}</span>;
    }
  };

  const openWizard = () => { setPresetClient(""); setWizardOpen(true); };

  return (
    <div data-testid="estimates-page">
      <PageHeader title="Sales & Estimates" description="Create estimates, send them to clients, and track acceptance & payment." />
      <SalesNav />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <FilterBar>
          <SearchBar value={query} onChange={setQuery} placeholder="Search estimates…" className="w-full sm:w-72" testId="estimates-search" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="estimates-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {FILTER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterBar>
        <ActionButton icon="Plus" onClick={openWizard} data-testid="new-estimate">Create Estimate</ActionButton>
      </div>
      <SectionCard>
        {loading ? <LoadingState label="Loading estimates…" /> : (
          <DataTable columns={columns} rows={rows} renderCell={renderCell} testId="estimates-table"
            empty={<EmptyState icon="Calculator" title="No estimates yet" description="Create your first estimate to get started."
              action={<ActionButton icon="Plus" onClick={openWizard}>Create Estimate</ActionButton>} />} />
        )}
      </SectionCard>
      <RowBinder testid="estimates-table" onRowClick={(id) => navigate(`/sales/estimates/${id}`)} />

      <CreateEstimateWizard open={wizardOpen} clientId={presetClient} onClose={() => setWizardOpen(false)}
        onCreated={(id) => { setWizardOpen(false); navigate(`/sales/estimates/${id}`); }} />
    </div>
  );
}

function RowBinder({ testid, onRowClick }) {
  useEffect(() => {
    const table = document.querySelector(`[data-testid="${testid}"]`);
    if (!table) return;
    const h = (e) => { const tr = e.target.closest('[data-testid^="row-"]'); if (tr) onRowClick(tr.getAttribute("data-testid").replace("row-", "")); };
    table.addEventListener("click", h); table.style.cursor = "pointer";
    return () => table.removeEventListener("click", h);
  });
  return null;
}
